/**
 * Servir las imágenes desde nuestro propio CDN en vez de desde Sharetribe.
 *
 * Sharetribe cobra por cada imagen que sirve, y además una cuota mensual por cada
 * foto distinta. Un Worker nuestro le pide cada foto UNA vez, la guarda y la sirve
 * a partir de entonces (ver `docs/sharetribe-credits.md`).
 *
 * La reescritura es solo de HOST: la ruta y los parámetros viajan intactos porque
 * llevan la firma de imgix dentro, y tocarlos la invalida.
 *
 * Hay DOS orígenes. Las fotos de anuncios y perfiles están en `sharetribe.imgix.net`, y
 * las imágenes que se suben por **Console** —el hero de la portada, las tarjetas de
 * ciudades, las secciones— en `sharetribe-assets.imgix.net`. Las segundas se nos escaparon
 * al principio: Sharetribe facturaba ~51.000 descargas al día que no pasaban por el CDN.
 * Van con el prefijo `/_assets/`, que el Worker quita antes de ir al origen (si no, la
 * firma no cuadra) y que mantiene separados los dos espacios de ids en la caché.
 *
 * Con `REACT_APP_IMAGE_HOST` sin definir no se reescribe nada y todo sigue yendo a
 * Sharetribe, que es el valor por defecto y la forma de revertir sin desplegar código.
 */

const SHARETRIBE_IMAGE_HOST = 'https://sharetribe.imgix.net';
const SHARETRIBE_ASSETS_HOST = 'https://sharetribe-assets.imgix.net';
const ASSETS_PREFIX = '/_assets';

// Dentro de la función, no en el módulo: así los tests pueden cambiar la variable.
const configuredHost = () => {
  const value = process.env.REACT_APP_IMAGE_HOST;
  return value ? value.trim().replace(/\/+$/, '') : null;
};

/**
 * @param {string} url URL de una variante de imagen de Sharetribe, de cualquiera de los dos orígenes
 * @returns {string} la misma URL servida por nuestro host, o la original
 */
export const rewriteImageUrl = url => {
  const host = configuredHost();
  if (!host || typeof url !== 'string') {
    return url;
  }
  if (url.startsWith(`${SHARETRIBE_IMAGE_HOST}/`)) {
    return `${host}${url.slice(SHARETRIBE_IMAGE_HOST.length)}`;
  }
  if (url.startsWith(`${SHARETRIBE_ASSETS_HOST}/`)) {
    return `${host}${ASSETS_PREFIX}${url.slice(SHARETRIBE_ASSETS_HOST.length)}`;
  }
  return url;
};

/**
 * La inversa de `rewriteImageUrl`: de nuestro host al origen de Sharetribe.
 *
 * Hace falta porque las URLs se reescriben **al entrar al almacén de entidades**, no al
 * pintar: así el HTML del servidor no publica ninguna URL de Sharetribe (el estado
 * precargado llevaba 696 por página de búsqueda, y cada una que pide un rastreador son 340
 * créditos de esa foto ese mes sin que el Worker la vea). Pero entonces `variant.url` ya es
 * la nuestra, y el repliegue de `ResponsiveImage` —si nuestro CDN falla, volver a
 * Sharetribe— necesita poder deshacerlo.
 *
 * Sin host configurado no hay nada que deshacer: el almacén lleva ya las de Sharetribe.
 *
 * @param {string} url una URL servida por nuestro host
 * @returns {string} la del origen de Sharetribe que le corresponde, o la misma
 */
export const originImageUrl = url => {
  const host = configuredHost();
  if (!host || typeof url !== 'string' || !url.startsWith(`${host}/`)) {
    return url;
  }
  const resto = url.slice(host.length);
  return resto.startsWith(`${ASSETS_PREFIX}/`)
    ? `${SHARETRIBE_ASSETS_HOST}${resto.slice(ASSETS_PREFIX.length)}`
    : `${SHARETRIBE_IMAGE_HOST}${resto}`;
};

/** True si esa URL la estamos sirviendo nosotros y no Sharetribe. */
export const isRewritten = url => {
  const host = configuredHost();
  return !!host && typeof url === 'string' && url.startsWith(`${host}/`);
};

// Un aviso por carga de página: si el CDN se cae, fallan TODAS las imágenes a la
// vez y no queremos mil peticiones ni mil mensajes en Slack.
let yaAvisado = false;

/**
 * El CDN propio ha fallado y hemos vuelto a Sharetribe. Avisa al servidor, que es
 * quien tiene el webhook de Slack. Best-effort: si esto falla, da igual.
 *
 * Va la URL COMPLETA, con su query: `onError` no ve el código de estado, así que el
 * servidor tiene que volver a pedir esa misma foto para saber si el CDN está caído o
 * si lo que falla es la URL (ver `server/api/image-cdn-fallback.js`). Sin la query,
 * que lleva la firma de imgix, la comprobación daría 403 siempre.
 */
export const reportImageCdnFallback = url => {
  if (yaAvisado || typeof window === 'undefined' || typeof fetch !== 'function') {
    return;
  }
  yaAvisado = true;
  try {
    fetch('/api/image-cdn-fallback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ host: configuredHost(), url: url || null }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    // Ni el aviso de un fallo puede romper la página.
  }
};

export { SHARETRIBE_IMAGE_HOST, SHARETRIBE_ASSETS_HOST };
